import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table, Tag, Button, Modal, Form, InputNumber, Input, Select, Alert, Tooltip, Empty, Space,
} from 'antd';
import { EditOutlined, ReloadOutlined, LockOutlined, SearchOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { libellesTraduits, avecLibelleTraduit } from '../../i18n/libelles';
import axiosInstance from '../../variables/api/axiosInstance';
import { can, HAB } from '../../variables/constants';

const GREEN = '#1a6b3c';
const MUTED = '#6b7280';

/**
 * Paramétrage des délais.
 *
 * <h3>Deux familles qu'il ne faut jamais confondre</h3>
 *
 * Les délais du référentiel n'ont pas tous la même autorité, et le backend le dit
 * déjà : `modifiable = false` marque un délai **imposé par la Convention Carte Brune
 * CEDEAO**, avec sa `referenceJuridique` en regard. Le bureau ne peut pas le
 * rallonger parce qu'il l'arrange - il s'impose à lui.
 *
 * `modifiable = true` marque au contraire un délai **interne au bureau** : un rythme
 * de relance, une échéance de traitement qu'on se fixe. Celui-là se règle librement.
 *
 * Les deux vivaient jusqu'ici dans un même tableau de l'écran « Délais & Alertes »,
 * distingués par un simple bouton grisé. Séparés en deux blocs titrés, la règle
 * devient lisible avant d'être découverte par un clic refusé.
 *
 * Le backend reste juge : `PUT /v1/delais/parametres/{id}` exige `DELAI_PARAMETRER`.
 * La garde ci-dessous ne fait qu'éviter d'afficher une action vouée au 403.
 */

const UNITES = libellesTraduits('parametres:delais.unites');

const CATEGORIES = [
  'GARANTIE', 'RESPONSABILITE', 'EXPERTISE', 'OFFRE', 'PAIEMENT',
  'RECOURS', 'PV', 'PRESCRIPTION', 'ARBITRAGE', 'INTERNE',
];

const TYPE_META = avecLibelleTraduit({
  DELAI_MAX: { color: 'blue' },
  DELAI_MIN: { color: 'cyan' },
  PENALITE_AUTO: { color: 'red' },
  PRESCRIPTION: { color: 'volcano' },
  INTERNE: { color: 'default' },
}, 'parametres:delais.types');

const pct = (v) => (v == null ? '-' : `${Number(v)} %`);

export default function ParametresDelais() {
  const { t } = useTranslation('parametres');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [categorie, setCategorie] = useState('');
  const [systeme, setSysteme] = useState([]);
  const [editionSysteme, setEditionSysteme] = useState({ open: false, row: null });
  const [formSysteme] = Form.useForm();
  const [edition, setEdition] = useState({ open: false, row: null });
  const [enregistrement, setEnregistrement] = useState(false);
  const [form] = Form.useForm();

  const peutParametrer = can(HAB.DELAI_PARAMETRER);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(null);
    try {
      // Les deux referentiels partagent l'ecran et l'habilitation DELAI_PARAMETRER.
      // Charges ensemble mais juges separement : un taux de calcul indisponible ne
      // doit pas faire disparaitre la table des delais, ni l'inverse.
      const [resDelais, resSysteme] = await Promise.allSettled([
        axiosInstance.get('/v1/delais/parametres'),
        axiosInstance.get('/v1/delais/parametres-systeme', { skipErrorToast: true }),
      ]);
      setSysteme(resSysteme.status === 'fulfilled' ? (resSysteme.value.data?.data || []) : []);
      if (resDelais.status === 'rejected') throw resDelais.reason;
      const res = resDelais.value;
      setRows(res.data?.data || []);
    } catch (err) {
      setErreur(err?.response?.data?.message || i18n.t('parametres:delais.messages.chargementImpossible'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const filtrees = useMemo(() => rows.filter((r) => {
    if (categorie && r.categorie !== categorie) return false;
    if (!recherche.trim()) return true;
    const q = recherche.trim().toLowerCase();
    return `${r.codeDelai || ''} ${r.libelle || ''} ${r.referenceJuridique || ''}`
      .toLowerCase().includes(q);
  }), [rows, categorie, recherche]);

  const reglementaires = filtrees.filter((r) => !r.modifiable);
  const internes = filtrees.filter((r) => r.modifiable);

  const ouvrirEdition = (row) => {
    form.setFieldsValue({
      valeur: row.valeur,
      seuilAlerte1Pct: row.seuilAlerte1Pct,
      seuilAlerte2Pct: row.seuilAlerte2Pct,
      tauxPenalitePct: row.tauxPenalitePct,
    });
    setEdition({ open: true, row });
  };

  const enregistrer = async () => {
    const vals = await form.validateFields();
    setEnregistrement(true);
    try {
      await axiosInstance.put(`/v1/delais/parametres/${edition.row.id}`, vals);
      toast.success(t('delais.messages.delaiMisAJour'));
      setEdition({ open: false, row: null });
      charger();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('delais.messages.miseAJourImpossible'));
    } finally {
      setEnregistrement(false);
    }
  };

  const ouvrirEditionSysteme = (row) => {
    formSysteme.setFieldsValue({ valeurDecimal: row.valeurDecimal });
    setEditionSysteme({ open: true, row });
  };

  const enregistrerSysteme = async () => {
    const vals = await formSysteme.validateFields();
    setEnregistrement(true);
    try {
      await axiosInstance.put(`/v1/delais/parametres-systeme/${editionSysteme.row.id}`, vals);
      toast.success(t('delais.messages.parametreMisAJour'));
      setEditionSysteme({ open: false, row: null });
      charger();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('delais.messages.miseAJourImpossible'));
    } finally {
      setEnregistrement(false);
    }
  };

  const colonnes = (editable) => [
    {
      title: t('delais.colonnes.code'),
      dataIndex: 'codeDelai',
      width: 150,
      render: (v) => (
        <span style={{
          fontSize: 12, background: '#f3f4f6', padding: '2px 6px',
          borderRadius: 4, fontFamily: 'var(--cb-font-mono)',
        }}>
          {v}
        </span>
      ),
    },
    { title: t('delais.colonnes.libelle'), dataIndex: 'libelle', ellipsis: true },
    {
      title: t('delais.colonnes.type'),
      dataIndex: 'typeDelai',
      width: 160,
      render: (v) => {
        const m = TYPE_META[v] || { label: v, color: 'default' };
        return <Tag color={m.color} style={{ borderRadius: 12, marginInlineEnd: 0 }}>{m.label}</Tag>;
      },
    },
    {
      title: t('delais.colonnes.valeur'),
      dataIndex: 'valeur',
      width: 150,
      align: 'right',
      render: (v, r) => (
        <span style={{ fontWeight: 700 }}>
          {v == null ? '-' : Number(v)}{' '}
          <span style={{ fontWeight: 400, color: MUTED, fontSize: 12 }}>
            {UNITES[r.unite] || r.unite || ''}
          </span>
        </span>
      ),
    },
    {
      title: t('delais.colonnes.alertes'),
      key: 'alertes',
      width: 130,
      align: 'right',
      render: (_, r) => (
        <span style={{ fontSize: 12, color: MUTED }}>
          {pct(r.seuilAlerte1Pct)} · {pct(r.seuilAlerte2Pct)}
        </span>
      ),
    },
    editable
      ? {
        title: '',
        key: 'action',
        width: 110,
        align: 'right',
        render: (_, r) => (
          <Button
            size="small"
            icon={<EditOutlined />}
            disabled={!peutParametrer}
            onClick={() => ouvrirEdition(r)}
          >
            {t('delais.actions.regler')}
          </Button>
        ),
      }
      : {
        title: t('delais.colonnes.reference'),
        dataIndex: 'referenceJuridique',
        width: 200,
        render: (v) => (
          <Tooltip title={v || t('delais.colonnes.aucuneReference')}>
            <span style={{ fontSize: 12, color: MUTED }}>
              <LockOutlined style={{ marginRight: 6 }} />
              {v || t('delais.colonnes.impose')}
            </span>
          </Tooltip>
        ),
      },
  ];

  const Bloc = ({ titre, description, data, editable }) => (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
        {titre} <span style={{ color: MUTED, fontWeight: 400 }}>({data.length})</span>
      </h3>
      <p style={{ color: MUTED, fontSize: 12.5, margin: '0 0 12px', maxWidth: 760, lineHeight: 1.6 }}>
        {description}
      </p>
      <Table
        className="cb-table"
        rowKey="id"
        size="middle"
        dataSource={data}
        columns={colonnes(editable)}
        loading={loading}
        pagination={false}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('delais.colonnes.aucunDelai')} /> }}
      />
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          placeholder={t('delais.filtres.recherche')}
          allowClear
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          style={{ width: 320 }}
        />
        <Select
          allowClear
          placeholder={t('delais.filtres.toutesCategories')}
          value={categorie || undefined}
          onChange={(v) => setCategorie(v || '')}
          options={CATEGORIES.map((c) => ({ value: c, label: c }))}
          style={{ width: 220 }}
        />
        <Button icon={<ReloadOutlined />} onClick={charger}>{t('delais.actions.actualiser')}</Button>
      </div>

      {!peutParametrer && (
        <Alert
          type="info"
          showIcon
          message={t('delais.page.consultationSeule')}
          style={{ marginBottom: 20 }}
        />
      )}

      {erreur && (
        <Alert
          type="error"
          showIcon
          message={erreur}
          action={<Button size="small" onClick={charger}>{t('delais.actions.reessayer')}</Button>}
          style={{ marginBottom: 20 }}
        />
      )}

      <Bloc
        titre={t('delais.blocs.internesTitre')}
        description={t('delais.blocs.internesDescription')}
        data={internes}
        editable
      />

      <Bloc
        titre={t('delais.blocs.conventionTitre')}
        description={t('delais.blocs.conventionDescription')}
        data={reglementaires}
        editable={false}
      />

      {/* Taux de calcul : ils ne sont ni des delais ni des alertes, mais ils
          relevent du meme geste - regler l'application sans redeploiement - et de
          la meme habilitation. Une rubrique de plus dans le menu pour deux lignes
          aurait ajoute au desordre qu'on cherche a reduire. */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
          {t('delais.taux.titre')} <span style={{ color: MUTED, fontWeight: 400 }}>({systeme.length})</span>
        </h3>
        <p style={{ color: MUTED, fontSize: 12.5, margin: '0 0 12px', maxWidth: 760, lineHeight: 1.6 }}>
          {t('delais.taux.description')}
        </p>
        <Table
          className="cb-table"
          rowKey="id"
          size="middle"
          dataSource={systeme}
          loading={loading}
          pagination={false}
          columns={[
            {
              title: t('delais.taux.cle'),
              dataIndex: 'cle',
              width: 260,
              render: (v) => (
                <span style={{
                  fontSize: 12, background: '#f3f4f6', padding: '2px 6px',
                  borderRadius: 4, fontFamily: 'var(--cb-font-mono)',
                }}>
                  {v}
                </span>
              ),
            },
            {
              title: t('delais.colonnes.libelle'),
              dataIndex: 'libelle',
              render: (v, r) => (
                <div>
                  <div>{v}</div>
                  {r.description && (
                    <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>{r.description}</div>
                  )}
                </div>
              ),
            },
            {
              title: t('delais.colonnes.valeur'),
              dataIndex: 'valeurDecimal',
              width: 130,
              align: 'right',
              render: (v) => <span style={{ fontWeight: 700 }}>{v == null ? '-' : Number(v)}</span>,
            },
            {
              title: '',
              key: 'action',
              width: 110,
              align: 'right',
              render: (_, r) => (
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled={!peutParametrer}
                  onClick={() => ouvrirEditionSysteme(r)}
                >
                  {t('delais.actions.regler')}
                </Button>
              ),
            },
          ]}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('delais.taux.aucunTaux')} /> }}
        />
      </div>

      <Modal
        open={editionSysteme.open}
        title={editionSysteme.row?.libelle}
        onCancel={() => setEditionSysteme({ open: false, row: null })}
        onOk={enregistrerSysteme}
        okText={t('delais.actions.enregistrer')}
        cancelText={t('delais.actions.annuler')}
        confirmLoading={enregistrement}
        okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}
        destroyOnClose
      >
        {editionSysteme.row?.description && (
          <p style={{ color: MUTED, fontSize: 12.5, marginTop: 0 }}>{editionSysteme.row.description}</p>
        )}
        <Form form={formSysteme} layout="vertical">
          <Form.Item
            label={t('delais.edition.valeur')}
            name="valeurDecimal"
            rules={[{ required: true, message: t('delais.edition.valeurObligatoire') }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={edition.open}
        title={<Space><EditOutlined /> {edition.row?.libelle}</Space>}
        onCancel={() => setEdition({ open: false, row: null })}
        onOk={enregistrer}
        okText={t('delais.actions.enregistrer')}
        cancelText={t('delais.actions.annuler')}
        confirmLoading={enregistrement}
        okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}
        destroyOnClose
      >
        <p style={{ color: MUTED, fontSize: 12.5, marginTop: 0 }}>
          {t('delais.edition.explicationSeuils')}
        </p>
        <Form form={form} layout="vertical">
          <Form.Item
            label={t('delais.edition.valeurUnite', { unite: UNITES[edition.row?.unite] || edition.row?.unite || '' })}
            name="valeur"
            rules={[{ required: true, message: t('delais.edition.valeurObligatoire') }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('delais.edition.premierSeuil')} name="seuilAlerte1Pct">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('delais.edition.secondSeuil')} name="seuilAlerte2Pct">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('delais.edition.tauxPenalite')} name="tauxPenalitePct">
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
